import {
  Button,
  Chip,
  CircularProgress,
  createStyles,
  Grid,
  IconButton,
  LinearProgress,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core"
import EditIcon from "@material-ui/icons/Edit"
import { Heading, ViraCard, ContentTabs, ViraText } from "@ksana/vira-ui"
import clsx from "clsx"
import { useEffect, useState, type MouseEvent, type ReactNode } from "react"
import { formatDateTime } from "../../../utils/dateTimeUtils"
import { colors } from "../../../utils/theme"
import { useSession } from "../../../hooks/useSession"
import {
  fetchProgressNote,
  ProgressNoteResponse,
  TranscriptFeedbackItem,
  TranscriptScoreResult,
  TranscriptSessionDetail as TranscriptSessionDetailData,
  TranscriptSessionModality,
  TranscriptSessionSummary,
} from "../../../utils/transcriptApi"
import TranscriptStatusChip from "./TranscriptStatusChip"
import TranscriptView from "./TranscriptView"

export type TranscriptDetailTab =
  | "scores"
  | "transcript"
  | "actionPlan"
  | "progressNote"
type ProgressNoteFormat = "SOAP" | "BIRP"

interface TranscriptSessionDetailProps {
  selectedSession: TranscriptSessionDetailData | null
  selectedSessionId: string | null
  detailLoading: boolean
  detailTab: TranscriptDetailTab
  activeSegment: number | null
  isRenaming: boolean
  onOpenRename: () => void
  onDetailTabChange: (detailTab: TranscriptDetailTab) => void
  onJumpToSegment: (segmentIndex: number) => void
  onEditProgressNote?: (sessionId: string) => void
  /** Client Detail: Action plan tab body for the selected session. */
  actionPlanContent?: ReactNode
  /** Client Detail: Progress note tab editor for the selected session. */
  progressNoteContent?: ReactNode
  /**
   * Always-visible Session Status strip (above Scores / Transcript / Action plan /
   * Progress note tabs).
   */
  inputsBanner?: ReactNode
  variant?: "default" | "clientDetail"
  /** Empty Transcript tab: upload a file into this session. */
  onUploadTranscriptForSession?: (file: File) => void
  /** Empty Transcript tab: start recording into this session. */
  onRecordForSession?: (event: MouseEvent<HTMLElement>) => void
  isAttachingTranscript?: boolean
  isRecordingBusy?: boolean
}

/**
 * Renders the selected transcript session's loading, processing, score,
 * transcript, and finalized progress-note states.
 */
export default function TranscriptSessionDetail({
  selectedSession,
  selectedSessionId,
  detailLoading,
  detailTab,
  activeSegment,
  isRenaming,
  onOpenRename,
  onDetailTabChange,
  onJumpToSegment,
  onEditProgressNote,
  actionPlanContent,
  progressNoteContent,
  inputsBanner,
  variant = "default",
  onUploadTranscriptForSession,
  onRecordForSession,
  isAttachingTranscript = false,
  isRecordingBusy = false,
}: TranscriptSessionDetailProps) {
  const classes = useStyles()
  const isClientDetail = variant === "clientDetail"
  const { session } = useSession()
  const [progressNote, setProgressNote] = useState<ProgressNoteResponse | null>(
    null
  )
  const [progressNoteLoading, setProgressNoteLoading] = useState(false)
  const [progressNoteError, setProgressNoteError] = useState<string | null>(
    null
  )
  const [progressNoteFormat, setProgressNoteFormat] =
    useState<ProgressNoteFormat>("SOAP")

  useEffect(() => {
    if (
      !session?.jwt ||
      !selectedSessionId ||
      selectedSession?.session.status !== "complete"
    ) {
      setProgressNote(null)
      setProgressNoteError(null)
      return
    }

    let ignore = false

    const loadProgressNote = async () => {
      try {
        setProgressNoteLoading(true)
        setProgressNoteError(null)
        const note = await fetchProgressNote(session.jwt, selectedSessionId)
        if (!ignore) {
          setProgressNote(note)
          if (note.status === "signed") {
            setProgressNoteFormat(
              note.soapText.trim()
                ? "SOAP"
                : note.birpText.trim()
                ? "BIRP"
                : "SOAP"
            )
          }
        }
      } catch (error) {
        if (!ignore) {
          setProgressNote(null)
          setProgressNoteError(
            error instanceof Error && error.message
              ? error.message
              : "Unable to load the progress note."
          )
        }
      } finally {
        if (!ignore) {
          setProgressNoteLoading(false)
        }
      }
    }

    void loadProgressNote()

    return () => {
      ignore = true
    }
  }, [selectedSession?.session.status, selectedSessionId, session?.jwt])

  return (
    <DetailShell
      isClientDetail={isClientDetail}
      className={classes.detailPanel}
    >
      {!selectedSessionId ? (
        <Typography variant="body2" className={classes.emptyState}>
          Select a transcript session to review its scores and transcript.
        </Typography>
      ) : detailLoading || !selectedSession ? (
        <div className={classes.loadingState}>
          <CircularProgress />
        </div>
      ) : (
        <>
          <div className={classes.detailHeader}>
            <div>
              <div className={classes.detailTitleRow}>
                {isClientDetail ? (
                  <Heading level="card">
                    {selectedSession.session.title || "Session"}
                  </Heading>
                ) : (
                  <Typography variant="h3" className={classes.sectionTitle}>
                    {selectedSession.session.title || "Session"}
                  </Typography>
                )}
                <IconButton
                  size="small"
                  className={classes.renameButton}
                  aria-label="edit transcript session details"
                  onClick={onOpenRename}
                  disabled={isRenaming}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </div>
              <Typography variant="body2" className={classes.sectionSubtitle}>
                {formatDateTime(selectedSession.session.createdAt)}
              </Typography>
            </div>
            <div className={classes.detailActions}>
              {selectedSession.session.modality && (
                <Chip
                  size="small"
                  label={formatModality(selectedSession.session.modality)}
                  className={classes.modalityChip}
                />
              )}
              <TranscriptStatusChip status={selectedSession.session.status} />
            </div>
          </div>

          {inputsBanner ? (
            <div className={classes.inputsBanner}>{inputsBanner}</div>
          ) : null}
          <div className={classes.detailTabs}>
            <ContentTabs<TranscriptDetailTab>
              aria-label="Session detail"
              options={[
                ...(actionPlanContent != null
                  ? [{ value: "actionPlan" as const, label: "Plan" }]
                  : []),
                { value: "progressNote", label: "Note" },
                { value: "transcript", label: "Transcript" },
                { value: "scores", label: "Score" },
              ]}
              value={detailTab}
              onChange={onDetailTabChange}
            />
          </div>

          {detailTab === "scores" ? (
            selectedSession.session.status !== "complete" ? (
              <div className={classes.tabLayout}>
                <ViraText tone="muted" sx={{ fontSize: 14 }}>
                  Review adherence scores and feedback from this session once
                  scoring finishes.
                </ViraText>
                <ProcessingState session={selectedSession.session} />
              </div>
            ) : (
              <ScoreView
                scoreResult={selectedSession.scoreResult}
                onJumpToSegment={onJumpToSegment}
              />
            )
          ) : detailTab === "transcript" ? (
            <TranscriptView
              transcript={selectedSession.transcript}
              highlights={selectedSession.scoreResult?.highlights ?? []}
              activeSegment={activeSegment}
              onUploadTranscript={onUploadTranscriptForSession}
              onRecordSession={onRecordForSession}
              isUploading={isAttachingTranscript}
              isRecordingBusy={isRecordingBusy}
              sessionStatus={
                isAttachingTranscript &&
                selectedSession.session.status === "complete"
                  ? "uploading"
                  : selectedSession.session.status
              }
              errorMessage={selectedSession.session.errorMessage}
            />
          ) : null}

          {/* Keep Action plan mounted so Session Status stays live across tabs. */}
          {actionPlanContent != null ? (
            <div
              className={classes.planAndNoteEmbed}
              hidden={detailTab !== "actionPlan"}
              aria-hidden={detailTab !== "actionPlan"}
            >
              {actionPlanContent}
            </div>
          ) : null}

          {detailTab === "progressNote" ? (
            progressNoteContent != null ? (
              <div className={classes.planAndNoteEmbed}>
                {progressNoteContent}
              </div>
            ) : (
              <ProgressNoteView
                progressNote={progressNote}
                loading={progressNoteLoading}
                errorMessage={progressNoteError}
                noteFormat={progressNoteFormat}
                onNoteFormatChange={setProgressNoteFormat}
                onEditProgressNote={
                  onEditProgressNote && selectedSessionId
                    ? () => onEditProgressNote(selectedSessionId)
                    : undefined
                }
              />
            )
          ) : null}
        </>
      )}
    </DetailShell>
  )
}

function DetailShell({
  isClientDetail,
  className,
  children,
}: {
  isClientDetail: boolean
  className: string
  children: ReactNode
}) {
  if (isClientDetail) {
    return (
      <ViraCard padded sx={{ maxWidth: "100%", minHeight: 480 }}>
        {children}
      </ViraCard>
    )
  }
  return (
    <Paper className={className} elevation={0}>
      {children}
    </Paper>
  )
}

function ProgressNoteView({
  progressNote,
  loading,
  errorMessage,
  noteFormat,
  onNoteFormatChange,
  onEditProgressNote,
}: {
  progressNote: ProgressNoteResponse | null
  loading: boolean
  errorMessage: string | null
  noteFormat: ProgressNoteFormat
  onNoteFormatChange: (format: ProgressNoteFormat) => void
  onEditProgressNote?: () => void
}) {
  const classes = useStyles()

  if (loading) {
    return (
      <div className={classes.loadingState}>
        <CircularProgress size={28} />
      </div>
    )
  }

  if (errorMessage) {
    return (
      <Typography variant="body2" className={classes.emptyState}>
        {errorMessage}
      </Typography>
    )
  }

  if (!progressNote || progressNote.status !== "signed") {
    return (
      <div>
        <Typography variant="body2" className={classes.emptyState}>
          No finalized progress note for this session.
        </Typography>
        {onEditProgressNote ? (
          <Button
            className={classes.editInPostSessionButton}
            color="primary"
            onClick={onEditProgressNote}
          >
            Edit in Post Session
          </Button>
        ) : null}
      </div>
    )
  }

  const activeNote =
    noteFormat === "SOAP" ? progressNote.soapText : progressNote.birpText
  const paragraphs = activeNote
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)

  return (
    <div>
      <div className={classes.progressNoteHeader}>
        <Chip
          label="Finalized"
          className={classes.finalizedChip}
          size="small"
        />
        <div className={classes.progressNoteToggle}>
          <Button
            size="small"
            variant={noteFormat === "SOAP" ? "contained" : "text"}
            color="primary"
            onClick={() => onNoteFormatChange("SOAP")}
          >
            SOAP
          </Button>
          <Button
            size="small"
            variant={noteFormat === "BIRP" ? "contained" : "text"}
            color="primary"
            onClick={() => onNoteFormatChange("BIRP")}
          >
            BIRP
          </Button>
        </div>
      </div>
      {paragraphs.length === 0 ? (
        <Typography variant="body2" className={classes.emptyState}>
          No {noteFormat} note was saved for this session.
        </Typography>
      ) : (
        paragraphs.map((paragraph, index) => {
          const section = splitSectionParagraph(paragraph)
          return (
            <Typography
              key={`${noteFormat}-${index}`}
              variant="body1"
              className={classes.progressNoteParagraph}
            >
              {section ? (
                <>
                  <span className={classes.progressNoteLabel}>
                    {section.label}
                  </span>{" "}
                  <span>{section.body}</span>
                </>
              ) : (
                paragraph
              )}
            </Typography>
          )
        })
      )}
      {onEditProgressNote ? (
        <Button
          className={classes.editInPostSessionButton}
          color="primary"
          startIcon={<EditIcon />}
          onClick={onEditProgressNote}
        >
          Edit in Post Session
        </Button>
      ) : null}
    </div>
  )
}

function splitSectionParagraph(paragraph: string): {
  label: string
  body: string
} | null {
  const match = paragraph.match(/^([A-Za-z]+\.)\s*([\s\S]*)$/)
  if (!match) {
    return null
  }
  return { label: match[1], body: match[2] }
}

function formatModality(modality: TranscriptSessionModality): string {
  return modality === "in_person" ? "In Person" : "Telehealth"
}

function ProcessingState({ session }: { session: TranscriptSessionSummary }) {
  const classes = useStyles()
  const isError = session.status === "error"
  const statusMessage =
    session.status === "uploading"
      ? "Uploading and preparing the transcript…"
      : session.status === "transcribing"
      ? "Extracting text and labeling speakers…"
      : session.status === "scoring"
      ? "Scoring the session against adherence criteria…"
      : isError
      ? session.errorMessage ||
        "This session could not be processed. Please try again or contact support if it continues."
      : "Preparing session detail…"

  if (isError) {
    return (
      <div className={classes.errorBanner} role="alert">
        {statusMessage}
      </div>
    )
  }

  return (
    <div className={classes.processingCard}>
      <LinearProgress className={classes.processingProgress} />
      <ViraText tone="muted" sx={{ fontSize: 14 }}>
        {statusMessage}
      </ViraText>
    </div>
  )
}

function ScoreView({
  scoreResult,
  onJumpToSegment,
}: {
  scoreResult: TranscriptScoreResult | null
  onJumpToSegment: (segmentIndex: number) => void
}) {
  const classes = useStyles()

  if (!scoreResult) {
    return (
      <div className={classes.tabLayout}>
        <ViraText tone="muted" sx={{ fontSize: 14 }}>
          Review adherence scores and feedback from this session once scoring
          finishes.
        </ViraText>
        <ViraText tone="muted" sx={{ fontSize: 14 }}>
          Score results are not available for this session.
        </ViraText>
      </div>
    )
  }

  return (
    <div className={classes.tabLayout}>
      <ViraText tone="muted" sx={{ fontSize: 14 }}>
        Review adherence scores and feedback from this session.
      </ViraText>
      <div className={classes.scoresContent}>
        <Paper className={classes.overallScoreCard} elevation={0}>
          <Typography variant="h2" className={classes.overallScoreValue}>
            {Math.round(scoreResult.overallScore)}
          </Typography>
          <div>
            <Typography variant="body1" className={classes.contentTitle}>
              Overall Quality Score
            </Typography>
            <Typography variant="body2" className={classes.sectionSubtitle}>
              Confidence {scoreResult.confidence || "unavailable"}
            </Typography>
          </div>
        </Paper>

        <Grid container spacing={2}>
          {scoreResult.dimensions.map((dimension) => (
            <Grid item xs={12} md={6} key={dimension.name}>
              <Paper className={classes.dimensionCard} elevation={0}>
                <div className={classes.dimensionHeader}>
                  <Typography variant="body1" className={classes.dimensionName}>
                    {dimension.name}
                  </Typography>
                  <Typography
                    variant="body1"
                    className={classes.dimensionScore}
                  >
                    {dimension.isNa ? "N/A" : Math.round(dimension.score)}
                  </Typography>
                </div>
                {!dimension.isNa && (
                  <LinearProgress
                    variant="determinate"
                    value={Math.max(0, Math.min(100, dimension.score))}
                    className={classes.dimensionProgress}
                  />
                )}
                {dimension.rationale && (
                  <Typography
                    variant="body2"
                    className={classes.dimensionRationale}
                  >
                    {dimension.rationale}
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} className={classes.feedbackGrid}>
          <Grid item xs={12} md={6}>
            <FeedbackColumn
              title="Strengths"
              items={scoreResult.strengths}
              tone="strength"
              onJumpToSegment={onJumpToSegment}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FeedbackColumn
              title="Areas for Attention"
              items={scoreResult.areasForAttention}
              tone="attention"
              onJumpToSegment={onJumpToSegment}
            />
          </Grid>
        </Grid>
      </div>
    </div>
  )
}

function FeedbackColumn({
  title,
  items,
  tone,
  onJumpToSegment,
}: {
  title: string
  items: TranscriptFeedbackItem[]
  tone: "strength" | "attention"
  onJumpToSegment: (segmentIndex: number) => void
}) {
  const classes = useStyles()
  const panelClassName =
    tone === "strength" ? classes.strengthPanel : classes.attentionPanel

  return (
    <Paper
      className={clsx(classes.feedbackPanel, panelClassName)}
      elevation={0}
    >
      <Typography variant="body1" className={classes.contentTitle}>
        {title}
      </Typography>
      {items.length === 0 ? (
        <Typography variant="body2" className={classes.emptyState}>
          No items available.
        </Typography>
      ) : (
        items.map((item, index) => (
          <div key={`${title}-${index}`} className={classes.feedbackItem}>
            <Typography variant="body2" className={classes.feedbackText}>
              {item.text}
            </Typography>
            {item.excerpt && (
              <Typography variant="body2" className={classes.feedbackExcerpt}>
                "{item.excerpt}"
              </Typography>
            )}
            {item.suggestion && (
              <Typography
                variant="body2"
                className={classes.feedbackSuggestion}
              >
                {item.suggestion}
              </Typography>
            )}
            {item.segmentRefs.length > 0 && (
              <Button
                color="primary"
                size="small"
                className={classes.jumpLink}
                onClick={() => onJumpToSegment(item.segmentRefs[0])}
              >
                Jump to transcript →
              </Button>
            )}
          </div>
        ))
      )}
    </Paper>
  )
}

const useStyles = makeStyles((theme) =>
  createStyles({
    detailPanel: {
      border: `1px solid var(--vira-border, ${colors.neutral.clay})`,
      borderRadius: 4,
      backgroundColor: `var(--vira-paper, ${colors.neutral.white})`,
      minHeight: 480,
      padding: theme.spacing(3),
    },
    loadingState: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: 320,
    },
    emptyState: { color: "var(--vira-muted, #5C6578)" },
    tabLayout: {
      display: "grid",
      gap: theme.spacing(2),
      justifyItems: "start",
      width: "100%",
    },
    scoresContent: {
      width: "100%",
    },
    processingCard: {
      display: "grid",
      gap: theme.spacing(2),
      width: "100%",
      justifySelf: "stretch",
    },
    processingProgress: {
      width: "100%",
    },
    errorBanner: {
      width: "100%",
      justifySelf: "stretch",
      padding: theme.spacing(2),
      borderRadius: 4,
      backgroundColor: colors.secondary.blush,
      color: colors.secondary.crimson,
      fontSize: 14,
      lineHeight: 1.5,
      fontWeight: 400,
    },
    planAndNoteEmbed: {
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
    },
    inputsBanner: {
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      marginBottom: theme.spacing(2),
    },
    detailHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: theme.spacing(2),
      marginBottom: theme.spacing(3),
    },
    detailActions: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      flexShrink: 0,
    },
    detailTitleRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
    },
    sectionTitle: {
      color: "var(--vira-ink, #1E263C)",
      fontWeight: 700,
    },
    sectionSubtitle: {
      color: "var(--vira-muted, #5C6578)",
      marginTop: theme.spacing(0.5),
    },
    modalityChip: {
      height: 28,
      borderRadius: 8,
      backgroundColor: "var(--vira-soft-surface, #E8EEF9)",
      color: "var(--vira-ink, #1E263C)",
      fontSize: 13,
      fontWeight: 600,
      letterSpacing: "0.01em",
      lineHeight: 1.2,
      "& .MuiChip-label": {
        paddingLeft: 10,
        paddingRight: 10,
      },
    },
    contentTitle: {
      fontWeight: 700,
      color: "var(--vira-ink, #1E263C)",
      marginBottom: theme.spacing(1),
    },
    renameButton: { color: "var(--vira-ink, #1E263C)" },
    detailTabs: {
      marginBottom: theme.spacing(3),
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
    },
    overallScoreCard: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(3),
      padding: theme.spacing(3),
      border: `1px solid var(--vira-border, ${colors.neutral.clay})`,
      backgroundColor: `var(--vira-paper, ${colors.neutral.white})`,
      marginBottom: theme.spacing(3),
    },
    overallScoreValue: {
      minWidth: 96,
      color: "var(--vira-ink, #1E263C)",
      fontWeight: 700,
    },
    dimensionCard: {
      height: "100%",
      padding: theme.spacing(2),
      border: `1px solid var(--vira-border, ${colors.neutral.clay})`,
      backgroundColor: `var(--vira-paper, ${colors.neutral.white})`,
    },
    dimensionHeader: {
      display: "flex",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    dimensionName: {
      color: "var(--vira-ink, #1E263C)",
      fontWeight: 700,
    },
    dimensionScore: {
      color: "var(--vira-ink, #1E263C)",
      fontWeight: 700,
    },
    dimensionProgress: {
      height: 8,
      borderRadius: 999,
      marginBottom: theme.spacing(1.5),
    },
    dimensionRationale: { color: "var(--vira-muted, #5C6578)" },
    feedbackGrid: { marginTop: theme.spacing(1) },
    feedbackPanel: {
      height: "100%",
      padding: theme.spacing(2),
      border: `1px solid var(--vira-border, ${colors.neutral.clay})`,
      backgroundColor: `var(--vira-paper, ${colors.neutral.white})`,
    },
    strengthPanel: {
      backgroundColor: "var(--vira-soft-surface, #F4FCF6)",
    },
    attentionPanel: {
      backgroundColor: "var(--vira-soft-surface, #FFF8ED)",
    },
    feedbackItem: {
      marginTop: theme.spacing(2),
      display: "grid",
      gap: theme.spacing(0.75),
    },
    feedbackText: { color: "var(--vira-ink, #1E263C)" },
    feedbackExcerpt: {
      color: "var(--vira-muted, #5C6578)",
      fontStyle: "italic",
    },
    feedbackSuggestion: { color: "var(--vira-ink, #1E263C)" },
    jumpLink: {
      justifySelf: "start",
      padding: 0,
      minWidth: 0,
      textTransform: "none",
      fontWeight: 600,
    },
    progressNoteHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: theme.spacing(2),
      marginBottom: theme.spacing(2),
      flexWrap: "wrap",
    },
    progressNoteToggle: {
      display: "inline-flex",
      gap: theme.spacing(1),
    },
    finalizedChip: {
      backgroundColor: "var(--vira-soft-surface, #E8EEF9)",
      color: "var(--vira-ink, #1E263C)",
      fontWeight: 700,
    },
    progressNoteParagraph: {
      color: "var(--vira-muted, rgba(30, 38, 60, 0.85))",
      fontSize: 13.5,
      lineHeight: "22.95px",
      marginBottom: theme.spacing(2),
      whiteSpace: "pre-wrap",
    },
    progressNoteLabel: {
      fontWeight: 700,
      color: "var(--vira-ink, #1E263C)",
    },
    editInPostSessionButton: {
      marginTop: theme.spacing(2),
      textTransform: "none",
      fontWeight: 700,
      paddingLeft: 0,
      minWidth: 0,
    },
  })
)
